import React, { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import i18n from "../i18n";
import axios from "axios";
import Swal from "sweetalert2";
import { convertToTimezone, TIMEZONES } from "../utilities/convertToTimezone";
import RbacManager from "../utilities/rbac";
import CustomToolbar from "../components/CustomToolbar";
import LoadingSpinner from "../components/LoadingSpinner";
import BankTransactionsCard from "../components/BankTransactionsCard";
import TableMobile from "../components/TableMobile";
import {
  DataGrid,
  useGridApiRef,
} from "@mui/x-data-grid";
import Pagination from "../components/Pagination";
import SkeletonWrapper from "../components/SkeletonWrapper";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const getStatusClass = (status) => {
  switch (status?.toLowerCase()) {
    case "approved":
      return "status-approved";
    case "open":
      return "status-open";
    case "rejected":
      return "status-rejected";
    case "cancelled":
      return "status-cancelled";
    default:
      return "status-pending";
  }
};
const BankTransactions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token, user, logout } = useAuth();
  const currentLanguage = i18n.language;
  const isArabic = currentLanguage === "ar";

  const [transactions, setTransactions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("pending");

  // Pagination + sorting
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [sortModel, setSortModel] = useState([]);
  const [filters, setFilters] = useState({ status: "pending" });
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [shouldFetchData, setShouldFetchData] = useState(false);
  const gridApiRef = useGridApiRef();
  const contentRef = useRef(null);
  const [isAtTop, setIsAtTop] = useState(true);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    console.log("isMobile", isMobile);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = contentRef.current?.scrollTop || 0;
      setIsAtTop(scrollTop < 20);
    };

    const container = contentRef.current;
    container?.addEventListener("scroll", handleScroll);
    return () => container?.removeEventListener("scroll", handleScroll);
  }, []);

  // RBAC
  const rbacMgr = new RbacManager(
    user?.userType === "employee" && user?.roles[0] !== "admin"
      ? user?.designation
      : user?.roles[0],
    "BankTransactions"
  );
  const isV = rbacMgr.isV.bind(rbacMgr);

  const role = user?.userType === "employee" ? user?.designation : user?.roles[0];
  const pageName = "BankTransactions";
  const storageKey = `${pageName}_${role}_columns`;

  useEffect(() => {
    const savedModel = localStorage.getItem(storageKey);
    if (savedModel) {
      setColumnVisibilityModel(JSON.parse(savedModel));
    }
  }, [storageKey]);
  const [showHeader, setShowHeader] = useState(true);
  const dragStartY = useRef(0);

  useEffect(() => {
    const handleTouchStart = (e) => {
      dragStartY.current = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      const currentY = e.touches[0].clientY;

      // Drag up → hide header
      if (currentY < dragStartY.current - 15) {
        setShowHeader(false);
      }

      // Drag down → show header
      if (currentY > dragStartY.current + 15) {
        setShowHeader(true);
      }
    };

    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchmove", handleTouchMove);

    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
    };
  }, []);

  // Fetch API
  const fetchTransactions = useCallback(
    async (page = 1, searchTerm = "", customFilters = {}, sortedModel = []) => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          page,
          pageSize,
          search: searchTerm,
          sortBy: sortedModel[0]?.field || "id",
          sortOrder: sortedModel[0]?.sort || "desc",
          filters: JSON.stringify(customFilters),
        });

        const { data } = await axios.get(
          `${API_BASE_URL}/bank-transactions/pagination?${params.toString()}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const formattedData = (data.data || []).map((row) => ({
          ...row,
          id: row.id,
          transactionId: row.transactionId || "",
          erpCustId: row.erpCustId || "",
          customerName: isArabic ? row.companyNameAr : row.companyNameEn,
          entity: row.entity || "",
          assignedSalesExecutive: row.assignedSalesExecutive || "",
          salesPersonRegion: Array.isArray(row.salesPersonRegion) ? row.salesPersonRegion.join(", ") : row.salesPersonRegion || "",
          orderId: Array.isArray(row.orderId) ? row.orderId.join(", ") : row.orderId || "",
          erpOrderId: Array.isArray(row.erpOrderId) ? row.erpOrderId.join(", ") : row.erpOrderId || "",
          transactionDate: convertToTimezone(row.transactionDate, TIMEZONES.SAUDI_ARABIA, "DD/MM/YYYY"),
          createdAt: convertToTimezone(row.createdAt, TIMEZONES.SAUDI_ARABIA, "DD/MM/YYYY"),
          modifiedByName: row.modifiedByName || "",
          status: row.status,
        }));

        setTransactions(formattedData);
        setTotal(data.pagination?.totalPages || 1);
      } catch (err) {
        console.error("Error fetching transactions:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [pageSize, token, isArabic]
  );

  useEffect(() => {
    const savedFilters = localStorage.getItem('BankTransactionFilters');
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        if (parsed.filters) setFilters(parsed.filters);
        if (parsed.searchQuery) setSearchQuery(parsed.searchQuery);
        if (parsed.page) setPage(parsed.page);
      } catch (error) {
        console.error('Error parsing saved filters:', error);
      }
    }
    setFiltersInitialized(true);
    setShouldFetchData(true); // Mark that we can now fetch data
  }, []);

  // Fetch data when user is available AND filters are initialized
  useEffect(() => {
    if (user && filtersInitialized) {
      fetchTransactions(page, searchQuery, filters, sortModel);
      setShouldFetchData(false); // Reset to prevent refetching
    }
  }, [user, filtersInitialized, shouldFetchData, page, searchQuery, filters, sortModel, fetchTransactions]);

  // Save filters to localStorage when they change
  useEffect(() => {
    if (filtersInitialized) {
      const filtersToSave = {
        filters,
        searchQuery,
        page
      };
      localStorage.setItem('BankTransactionFilters', JSON.stringify(filtersToSave));
      console.log("Filters saved to localStorage:", filtersToSave);
    }
  }, [filters, searchQuery, filtersInitialized, page]);

  // Handle search
  const handleSearch = (searchTerm) => {
    setSearchQuery(searchTerm);
    setPage(1);
  };

  // Handle sort
  const handleSortModelChange = (model) => {
    setSortModel(model);
    setPage(1); // Reset to page 1 when sorting
    // Don't fetch here, let the useEffect handle it
  };

  const handleFilterChange = (newFilters) => {
    // Preserve the status filter from the active tab
    const updatedFilters = { ...newFilters };
    if (activeTab === "pending") {
      updatedFilters.status = "pending";
    }
    setFilters(updatedFilters);
    setPage(1);
    setFilterAnchor(null);
  };

  // Handle tab change for Pending/All
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setPage(1);

    if (newTab === "pending") {
      // Apply pending status filter, preserve other filters
      const { status, ...otherFilters } = filters;
      setFilters({ ...otherFilters, status: "pending" });
    } else {
      // Remove status filter for "all", preserve other filters
      const { status, ...otherFilters } = filters;
      setFilters(otherFilters);
    }
  };

  const downloadBankTransactionsAsExcel = async () => {
    const confirm = await Swal.fire({
      title: t("Confirm Download?"),
      text: t("Are you sure you want to download bank transactions?"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("Yes, download"),
      cancelButtonText: t("No, cancel"),
    });

    if (!confirm.isConfirmed) return;

    try {
      setLoading(true);

      /* -------- Show same loading message -------- */
      Swal.fire({
        title: t("Preparing Export"),
        text: t("Fetching bank transactions, please wait..."),
        icon: "info",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });

      const params = new URLSearchParams({
        search: searchQuery,
        sortBy: sortModel[0]?.field || "id",
        sortOrder: sortModel[0]?.sort || "desc",
        filters: JSON.stringify(filters),
      });

      const response = await axios.get(
        `${API_BASE_URL}/bank-transactions/export?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob' // Important for Excel file
        }
      );

      /* -------- Receive file as blob -------- */
      const blob = response.data;

      if (!blob || blob.size === 0) {
        throw new Error("Empty file received");
      }

      /* -------- Extract filename from header -------- */
      const contentDisposition = response.headers['content-disposition'];
      let filename = "bank_transactions_export.xlsx";

      if (contentDisposition) {
        // RFC 5987 (filename*=)
        const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (utf8Match) {
          filename = decodeURIComponent(utf8Match[1]);
        } else {
          // Normal filename=
          const asciiMatch = contentDisposition.match(/filename=\"?([^\"]+)\"?/i);
          if (asciiMatch) {
            filename = asciiMatch[1];
          }
        }
      }

      /* -------- Download file -------- */
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      Swal.fire({
        title: t("Export Successful"),
        text: t("Bank transactions exported successfully."),
        icon: "success",
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        title: t("Export Failed"),
        text: error.message || t("Failed to export bank transactions."),
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };


  // Handle row click
  const handleRowClick = (transaction) => {
    navigate(`/bankTransactions/edit/${transaction.id}`);
  };

  const handleAddClick = () => {
    navigate(`/bankTransactions/add`);
  };

  // Columns with minWidth for readable headers
  const transactionColumns = [
    {
      field: "transactionId",
      headerName: t("Transaction ID"),
      include: isV("TransactionIdCol"),
      searchable: true,
      sortable: false,
      flex: 1,
      minWidth: 130
    },
    {
      field: "erpCustId",
      headerName: t("ERP Customer ID"),
      include: isV("erpCustIdCol"),
      searchable: true,
      sortable: false,
      flex: 1,
      minWidth: 150
    },
    {
      field: "customerName",
      headerName: t("Customer Name"),
      include: isV("customerCol"),
      searchable: true,
      sortable: false,
      flex: 2,
      minWidth: 180
    },
    {
      field: "entity",
      headerName: t("Business Unit"),
      include: isV("entityCol"),
      searchable: true,
      sortable: false,
      flex: 1,
      minWidth: 120
    },
    {
      field: "assignedSalesExecutive",
      headerName: t("Assigned Sales Executive"),
      include: isV("assignedSalesExecutiveCol"),
      searchable: true,
      sortable: false,
      flex: 1.5,
      minWidth: 200
    },
    {
      field: "salesPersonRegion",
      headerName: t("Sales Person Region"),
      include: isV("salesPersonRegionCol"),
      searchable: true,
      sortable: false,
      flex: 1.5,
      minWidth: 180
    },
    {
      field: "orderId",
      headerName: t("Order ID"),
      include: isV("OrderIdCol"),
      searchable: true,
      sortable: false,
      flex: 1,
      minWidth: 100
    },
    {
      field: "erpOrderId",
      headerName: t("ERP Order ID"),
      include: isV("erpOrderIdCol"),
      searchable: true,
      sortable: false,
      flex: 1,
      minWidth: 130
    },
    {
      field: "transactionDate",
      headerName: t("Transaction Date"),
      include: isV("transactionDateCol"),
      searchable: true,
      sortable: false,
      flex: 1,
      minWidth: 150
    },
    {
      field: "createdAt",
      headerName: t("Created Date"),
      include: isV("createdAtCol"),
      searchable: true,
      sortable: false,
      flex: 1,
      minWidth: 130
    },
    {
      field: "createdByName",
      headerName: t("Created By"),
      include: isV("createdByNameCol"),
      searchable: true,
      sortable: false,
      flex: 1,
      minWidth: 130
    },
    {
      field: "modifiedByName",
      headerName: t("Verified By"),
      include: isV("modifiedByNameCol"),
      searchable: true,
      sortable: false,
      flex: 1,
      minWidth: 130
    },
    {
      field: "status",
      headerName: t("Transaction Status"),
      include: isV("statusCol"),
      searchable: true,
      sortable: false,
      flex: 1,
      minWidth: 160,
       renderCell: (params) => (
        <label className={getStatusClass(params.value)}>
          {t(params.value)}
        </label>
      ),
    },
  ];

  const visibleColumns = transactionColumns.filter((col) => col.include !== false);
  const searchableFields = visibleColumns.filter((c) => c.searchable).map((c) => c.field);
  const filteredData = visibleColumns?.filter((item) =>
    searchableFields?.includes(item?.field)
  );

  const columnsToDisplay = {
    transactionId: "Transaction ID",
    erpCustId: "ERP Customer ID",
    customerName: "Customer Name",
    entity: "Business Unit",
    assignedSalesExecutive: "Assigned Sales Executive",
    salesPersonRegion: "Sales Person Region",
    orderId: "Order ID",
    erpOrderId: "ERP Order ID",
    transactionDate: "Transaction Date",
    createdAt: "Created Date",
    createdByName: "Created By",
    modifiedByName: "Verified By",
    status: "Transaction Status",
  };

  const handleColumnVisibilityChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem(storageKey, JSON.stringify(newModel));
  };

  return (
    <Sidebar title={t("Bank Transactions")} CardPaddingClass={true}>
      {isV("BankContent") && (
        <div className="bank-transactions-container">
          {isMobile ? (
            <div className="orders-content">
              {
                // loading ? (
                //   <div  className="container">
                //        <div  className="logoWrapper" style={{position: "fixed",
                //      top: "50%",
                //      left: "50%"}}>
                //          <LoadingSpinner/>
                //        </div>
                //        <style>
                //          {`

                //              container: {
                //      display: 'flex',
                //      justifyContent: 'center',
                //      alignItems: 'center',
                //      width: '100%',
                //      backgroundColor: '#ffffff',
                //      position: 'fixed', // Ensures it covers the whole screen
                //      top: 0,
                //      left: 0,
                //      zIndex: 9999,      // Keeps it above all other elements
                //    },
                //    logoWrapper: {
                //      animation: 'pulse 2s infinite ease-in-out',
                //    },
                //    logo: {
                //      width: '120px',    // Adjust size as needed
                //      height: 'auto',
                //    }
                //          `}
                //        </style>
                //      </div>
                // ) : 
                error ? (
                  <div className="error-message">{error}</div>
                ) : (
                  <>
                    <div
                      className={`catalog-fixed-header ${showHeader ? "show" : "show"} maintenance-bottom-padding`}
                    // style={{
                    //   top: isAtTop ? "60px" : "0px",
                    //   position: "sticky",
                    //   zIndex: 20,
                    //   transition: "top 0.3s ease",
                    //   background: "#fff",
                    // }}
                    >
                      <TableMobile
                        columns={visibleColumns}
                        allColumns={visibleColumns}
                        data={transactions}
                        showAllDetails={true}
                        disableExtendRowFullWidth={true}
                        dataGridComponent={
                          <DataGrid
                            apiRef={gridApiRef}
                            rows={transactions}
                            columns={visibleColumns}
                            pageSize={pageSize}
                            rowCount={total}
                            onRowClick={(params) => handleRowClick(params.row)}
                            columnVisibilityModel={columnVisibilityModel}
                            onColumnVisibilityModelChange={handleColumnVisibilityChange}
                            sortModel={sortModel}
                            onSortModelChange={handleSortModelChange}
                            disableSelectionOnClick
                            disableColumnMenu
                            hideFooter={true}
                            hideFooterPagination={true}
                            pagination={false}
                            rowHeight={55}
                            showToolbar
                            slots={{
                              toolbar: () => (
                                <CustomToolbar
                                  searchQuery={searchQuery}
                                  filterAnchor={filterAnchor}
                                  onSearch={handleSearch}
                                  setSearchQuery={setSearchQuery}
                                  setFilterAnchor={setFilterAnchor}
                                  handleFilterChange={handleFilterChange}
                                  onColumnVisibilityChange={handleColumnVisibilityChange}
                                  columns={filteredData}
                                  filters={filters}
                                  columnVisibilityModel={columnVisibilityModel}
                                  searchPlaceholder={t("Search transactions...")}
                                  showColumnVisibility={false}
                                  showFilters={false}
                                  showExport={isV('btnExport')}
                                  showUpload={false}
                                  showAdd={isV('btnAdd')}
                                  handleAddClick={handleAddClick}
                                  buttonName={t("add")}
                                  columnsToDisplay={columnsToDisplay}
                                  showTransactionTabs={true}
                                  activeTransactionTab={activeTab}
                                  handleTransactionTabChange={handleTabChange}
                                  excludeFiltersFromChips={["status"]}
                                />
                              ),
                            }}
                            sx={{
                              border: "none !important",
                              "& .MuiDataGrid-overlay": {
                                display: "none !important",
                              },
                              "& .MuiDataGrid-row": {
                                display: "none !important",
                              },
                              ".MuiDataGrid-cell": {
                                display: "none !important",
                              },
                              "& .MuiDataGrid-main": {
                                display: "none",
                              },
                              "& .MuiDataGrid-toolbar": {
                                padding: "0px 8px",
                                gap: "10px",
                                border: "none",
                              },
                              "&.catalog-datagrid": {
                                border: "2px solid black",
                                borderRadius: "8px",
                                backgroundColor: "#f8f9fa",
                              },
                              "& .MuiOutlinedInput-root": {
                                width: "100% !important",
                                minWidth: "230px !important",
                              }
                            }}
                          />
                        }
                      />
                    </div>
                    <div style={{ marginTop: "16px", position: "relative", zIndex: 1 }}>
                      <SkeletonWrapper loading={loading} type="order_card" count={4}>
                        <BankTransactionsCard
                          transactions={transactions}
                          setSelectedRow={handleRowClick}
                        />
                      </SkeletonWrapper>
                    </div>
                  </>
                )}
            </div>
          ) : (
            <div className="table-container">
              {
                // loading ? (
                //   <div className="loading-container" style={{ position: "absolute", top: "50%", left: "50%" }}>
                //     <LoadingSpinner size="medium" />
                //   </div>
                // ) : 
                error ? (
                  <div className="error-message">{error}</div>
                ) : (
                  <SkeletonWrapper loading={loading} type="table" rows={10} columns={5}>
                    <DataGrid
                      apiRef={gridApiRef}
                      rows={transactions}
                      columns={visibleColumns}
                      pageSize={pageSize}
                      rowCount={total}
                      onRowClick={(params) => handleRowClick(params.row)}
                      columnVisibilityModel={columnVisibilityModel}
                      onColumnVisibilityModelChange={handleColumnVisibilityChange}
                      sortModel={sortModel}
                      onSortModelChange={handleSortModelChange}
                      disableSelectionOnClick
                      disableColumnMenu
                      hideFooter={true}
                      hideFooterPagination={true}
                      pagination={false}
                      rowHeight={55}
                      showToolbar
                      slots={{
                        toolbar: () => (
                          <CustomToolbar
                            searchQuery={searchQuery}
                            filterAnchor={filterAnchor}
                            onSearch={handleSearch}
                            setSearchQuery={setSearchQuery}
                            setFilterAnchor={setFilterAnchor}
                            handleFilterChange={handleFilterChange}
                            onColumnVisibilityChange={handleColumnVisibilityChange}
                            columns={filteredData}
                            filters={filters}
                            columnVisibilityModel={columnVisibilityModel}
                            searchPlaceholder={t("Search transactions...")}
                            showColumnVisibility={true}
                            showFilters={true}
                            showExport={isV('btnExport')}
                            showUpload={false}
                            showAdd={isV('btnAdd')}
                            handleExportClick={downloadBankTransactionsAsExcel}
                            handleAddClick={handleAddClick}
                            buttonName={t("add")}
                            columnsToDisplay={columnsToDisplay}
                            showTransactionTabs={true}
                            activeTransactionTab={activeTab}
                            handleTransactionTabChange={handleTabChange}
                            excludeFiltersFromChips={["status"]}
                          />
                        ),
                      }}
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        '& .MuiDataGrid-main': {
                          flex: 1,
                          overflow: 'hidden'
                        },
                        '& .MuiDataGrid-toolbar': {
                          padding: '0px 8px  !important',
                          minHeight: '56px !important',
                          flexShrink: 0,
                        },
                        '& .MuiDataGrid-virtualScroller': {
                          flex: 1
                        },
                        '& .MuiDataGrid-columnHeaders': {
                          top: 0,
                          zIndex: 1,
                          backgroundColor: 'white',
                          borderBottom: '1px solid #e0e0e0',
                        },
                        '& .MuiDataGrid-row': {
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                        },
                        ...(isArabic && {
                          direction: "rtl",
                          "& .MuiDataGrid-cell": {
                            textAlign: "right !important",
                          },
                          "& .MuiDataGrid-columnHeader": {
                            textAlign: "right !important",
                          },
                          "& .MuiDataGrid-columnHeaderTitle": {
                            textAlign: "right !important",
                          },
                          "& .MuiDataGrid-cellContent": {
                            textAlign: "right !important",
                          }
                        }),
                        ...(!isArabic && {
                          "& .MuiDataGrid-cell": {
                            textAlign: "left",
                          },
                          "& .MuiDataGrid-columnHeader": {
                            textAlign: "left",
                          },
                          "& .MuiDataGrid-columnHeaderTitle": {
                            textAlign: "left",
                          },
                          "& .MuiDataGrid-cellContent": {
                            textAlign: "left",
                          }
                        })
                      }}
                    />
                  </SkeletonWrapper>
                )}
            </div>
          )}
          {transactions?.length > 0 && !loading && (
            <Pagination
              currentPage={page}
              totalPages={String(total)}
              onPageChange={setPage}
            />
          )}
        </div>
      )}
    </Sidebar>
  );
};

export default BankTransactions;